// Adapted from shadcn/ui (MIT). See ../../THIRD_PARTY.md for provenance and local changes.
import { Button as ButtonPrimitive } from "@base-ui/react/button";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "cn";
import { Loader, LoaderSizeContext } from "./loader";

/**
 * A pending button's loader, sized to the button's text: the loader is a solid square of blocks,
 * so at the text's own height it already reads heavier than the letters beside it.
 */
const LOADER_SIZE: Record<string, number> = { xs: 10, "icon-xs": 10, lg: 14, "icon-lg": 14 };

/**
 * One size for every button that ends a dialog: `--action-height` (32 with a mouse, 44 on touch)
 * and the compact type. `Dialog.Footer` and `Dialog.Actions` hold their buttons to the same.
 */
const ACTION_SIZE =
  "h-(--action-height) gap-1.5 rounded-[6px] px-3 text-[13px] has-data-[icon=inline-end]:pr-2 has-data-[icon=inline-start]:pl-2";

const buttonVariants = cva(
  // The press lands on the same frame as the pointer (active:duration-0) and eases back on
  // release, so a 1px travel reads as a click rather than a resize you can watch happen.
  "group/button inline-flex shrink-0 items-center justify-center rounded-(--control-radius) border bg-clip-border text-(length:--text-ui) leading-(--text-ui--line-height) font-medium whitespace-nowrap transition-[background-color,background-image,color,box-shadow,filter,transform] duration-fast ease-out outline-none select-none motion-reduce:transition-none focus-visible:border-(--focus-border) focus-visible:ring-3 focus-visible:ring-(--focus-halo) motion-safe:active:not-focus-visible:not-aria-[haspopup]:translate-y-px active:duration-0 disabled:pointer-events-none disabled:opacity-50 disabled:shadow-none data-disabled:pointer-events-none data-disabled:opacity-50 data-disabled:shadow-none aria-invalid:border-destructive aria-invalid:ring-3 aria-invalid:ring-destructive/20 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  {
    variants: {
      variant: {
        default:
          "border-transparent bg-linear-to-b from-(--button-primary-top) to-(--button-primary-bottom) text-primary-foreground shadow-(--shadow-button-primary) hover:from-(--button-primary-top-hover) hover:to-(--button-primary-bottom-hover) active:not-aria-[haspopup]:shadow-(--shadow-button-primary-active) disabled:bg-secondary disabled:bg-none disabled:text-subtle-foreground disabled:opacity-100 data-disabled:bg-secondary data-disabled:bg-none data-disabled:text-subtle-foreground data-disabled:opacity-100",
        outline:
          "border-(--button-secondary-border) bg-linear-to-b from-card to-(--button-secondary-bottom) text-foreground shadow-(--shadow-button-secondary) hover:brightness-[0.98] aria-expanded:brightness-[0.98] active:not-aria-[haspopup]:shadow-(--shadow-button-secondary-active)",
        secondary:
          "border-transparent bg-secondary text-secondary-foreground hover:bg-[color-mix(in_oklch,var(--secondary),var(--foreground)_5%)] aria-expanded:bg-secondary aria-expanded:text-secondary-foreground",
        ghost:
          "border-transparent hover:bg-muted hover:text-foreground aria-expanded:bg-muted aria-expanded:text-foreground",
        destructive:
          "border-(--button-secondary-border) bg-linear-to-b from-card to-(--button-secondary-bottom) text-destructive shadow-(--shadow-button-secondary) hover:brightness-[0.98] active:not-aria-[haspopup]:shadow-(--shadow-button-secondary-active) focus-visible:border-destructive focus-visible:ring-destructive/15",
        link: "border-transparent text-primary underline-offset-4 hover:underline",
      },
      size: {
        default:
          "h-(--control-height) gap-2 px-4 has-data-[icon=inline-end]:pr-2 has-data-[icon=inline-start]:pl-2",
        xs: "h-6 gap-1 rounded-[5px] px-2.5 text-[11px] has-data-[icon=inline-end]:pr-2 has-data-[icon=inline-start]:pl-2 [&_svg:not([class*='size-'])]:size-3",
        sm: "h-8 gap-1 rounded-[6px] px-3 text-[13px] has-data-[icon=inline-end]:pr-1.5 has-data-[icon=inline-start]:pl-1.5",
        /** A dialog's closing actions; `Dialog.Footer` and `Dialog.Actions` apply it for you. */
        action: ACTION_SIZE,
        lg: "h-11 gap-2 px-5 has-data-[icon=inline-end]:pr-2 has-data-[icon=inline-start]:pl-2",
        icon: "size-(--control-height)",
        "icon-xs": "size-6 rounded-[5px] [&_svg:not([class*='size-'])]:size-3",
        "icon-sm": "size-8 rounded-[6px]",
        "icon-lg": "size-11",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

/**
 * `pending` is the one way a button says its action is in progress: a loader before the label, the
 * button off (no press, no submit, no second send) and `aria-busy` for a screen reader. An icon
 * button has no label to put a loader before, so it keeps its icon and only dims. It is off
 * but still focusable, so pressing a button never throws focus to the page behind it, and a refused
 * action needs no code to hand focus back. Never leave a pending button looking pressable, and
 * never rebuild this by hand with a loader and a guard.
 */
function Button({
  className,
  variant = "default",
  size = "default",
  pending = false,
  disabled,
  focusableWhenDisabled,
  children,
  ...props
}: ButtonPrimitive.Props & VariantProps<typeof buttonVariants> & { pending?: boolean }) {
  return (
    <LoaderSizeContext.Provider value={LOADER_SIZE[size ?? "default"] ?? 12}>
      <ButtonPrimitive
        data-slot="button"
        className={(state) =>
          cn(
            buttonVariants({ variant, size }),
            typeof className === "function" ? className(state) : className,
          )
        }
        disabled={disabled || pending}
        focusableWhenDisabled={focusableWhenDisabled ?? pending}
        aria-busy={pending || undefined}
        {...props}
      >
        {pending && !size?.startsWith("icon") && <Loader />}
        {children}
      </ButtonPrimitive>
    </LoaderSizeContext.Provider>
  );
}

export { Button, buttonVariants };
